import { useEffect, useMemo } from 'react';

import { FeatureFlag } from '../enums/FeatureFlag';
import { buildContextWithKey } from '../functions/buildContextWithKey';
import useFeatureStore from '../useFeatureStore';

/**
 * Returns the variant name for a feature flag from cached state, or the provided
 * fallback when no variant is cached. Asks the store to fetch the latest value
 * and handle metrics outside of component rendering.
 */
export const useFeatureVariant = (
  flag: FeatureFlag,
  fallbackValue: string,
  context?: Record<string, unknown>,
): string => {
  const defaultContext = useFeatureStore(
    (state): Record<string, unknown> => state.defaultContext,
  );

  const contextJson = useMemo(
    (): string => JSON.stringify(context ?? {}),
    [context],
  );
  const parsedContext = useMemo(
    (): any => JSON.parse(contextJson),
    [contextJson],
  );

  const contextKey = useMemo((): string => {
    const [, key] = buildContextWithKey(defaultContext, parsedContext);
    return key;
  }, [defaultContext, parsedContext]);

  const cachedVariantName = useFeatureStore((state): string | undefined => {
    const variant = state.getCachedVariant(flag, parsedContext);

    return variant?.name;
  });

  // Trigger evaluation (fetch + metrics) outside render
  useEffect((): void => {
    useFeatureStore.getState().isEnabled(flag, parsedContext);
  }, [flag, contextKey]);

  return cachedVariantName ?? fallbackValue;
};
